import { Pt } from '../../../interfaces/symmetry.interface';

/**
 * Pure geometry helpers for the Mirror Match game. Kept free of Angular
 * so they're trivially unit-testable and reusable by any future
 * "reflect / fold" skill game.
 */

/** Reflect point `p` across the infinite line through `a` and `b`. */
export function reflectPoint(p: Pt, a: Pt, b: Pt): Pt {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-6) return { x: p.x, y: p.y }; // degenerate line
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return { x: 2 * projX - p.x, y: 2 * projY - p.y };
}

/** Reflect a whole polygon across the line through `a` and `b`. */
export function reflectPolygon(points: Pt[], a: Pt, b: Pt): Pt[] {
  return points.map(p => reflectPoint(p, a, b));
}

/**
 * True when the line through `a`,`b` is a line of symmetry of the
 * polygon — i.e. reflecting every vertex maps the vertex *set* onto
 * itself (a permutation) within `tol` units. Greedy nearest-match is
 * safe here because vertex spacing on the shape bank is far larger
 * than `tol`.
 */
export function isAxisOfSymmetry(points: Pt[], a: Pt, b: Pt, tol: number): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx * dx + dy * dy < 1e-6) return false; // no line
  const reflected = points.map(p => reflectPoint(p, a, b));
  const used = new Array(points.length).fill(false);
  const tol2 = tol * tol;
  for (const rp of reflected) {
    let best = -1;
    let bestD = tol2;
    for (let i = 0; i < points.length; i++) {
      if (used[i]) continue;
      const ddx = rp.x - points[i].x;
      const ddy = rp.y - points[i].y;
      const d = ddx * ddx + ddy * ddy;
      if (d <= bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best === -1) return false; // this vertex has no mirror partner
    used[best] = true;
  }
  return true;
}

/** Average of the vertices — the centre every axis passes through. */
export function centroid(points: Pt[]): Pt {
  const s = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: s.x / points.length, y: s.y / points.length };
}

/** Largest vertex distance from `c` — used to scale tolerance + beams. */
export function boundingRadius(points: Pt[], c: Pt): number {
  return points.reduce((m, p) => Math.max(m, Math.hypot(p.x - c.x, p.y - c.y)), 0);
}

/** Two endpoints of a line through `c` at `angleDeg`, length `2*len`. */
export function axisEndpoints(c: Pt, angleDeg: number, len: number): [Pt, Pt] {
  const r = (angleDeg * Math.PI) / 180;
  const dx = Math.cos(r) * len;
  const dy = Math.sin(r) * len;
  return [{ x: c.x - dx, y: c.y - dy }, { x: c.x + dx, y: c.y + dy }];
}

/** SVG polygon `points` attribute from an array of points. */
export function toPolygonPoints(points: Pt[]): string {
  return points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
}
